/**
 * Auth API
 * Аналог lib/features/auth/data/datasources/auth_remote_data_source.dart
 */
import { apiClient } from '@/api'
import type {
  AcceptInvitationPayload,
  AuthTokens,
  InvitationPreview,
  LoginRequest,
  LoginResponse,
  RefreshTokenResponse,
  UpdateUserSettingsParams,
  User,
  UserSettings,
} from '../types/auth.types'

export const authApi = {
  /**
   * Login - аналог AuthRemoteDataSource.login()
   * POST /api/v1/auth/login
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>('/api/v1/auth/login', {
      username: credentials.email,
      password: credentials.password,
    })
    return response.data
  },

  /**
   * ExchangeSSOCode — обмен одноразового кода, выданного после входа через
   * Google, на обычную пару токенов (DEV-2119).
   * POST /api/v1/auth/sso/exchange
   *
   * Ответ тот же, что у login: дальше вход через Google ничем не отличается.
   */
  async exchangeSSOCode(code: string): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>('/api/v1/auth/sso/exchange', { code })
    return response.data
  },

  /**
   * Logout
   * POST /api/v1/auth/logout
   */
  async logout(): Promise<void> {
    await apiClient.post('/api/v1/auth/logout')
  },

  /**
   * Refresh Token - аналог AuthRemoteDataSource.refreshToken()
   * POST /api/v1/auth/refresh
   */
  async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    const response = await apiClient.post<RefreshTokenResponse>('/api/v1/auth/refresh', {
      refreshToken,
    })
    return response.data
  },

  /**
   * Change Password — смена пароля текущим пользователем (DEV-705)
   * POST /api/v1/auth/password/change
   */
  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await apiClient.post('/api/v1/auth/password/change', {
      oldPassword,
      newPassword,
    })
  },

  /**
   * Get Current User - аналог GetCurrentUser usecase
   * GET /api/v1/auth/users/me
   */
  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get<User>('/api/v1/auth/me')
    return response.data
  },

  /**
   * Get User Settings
   * GET /api/v1/auth/settings
   */
  async getUserSettings(): Promise<UserSettings> {
    const response = await apiClient.get<UserSettings>('/api/v1/auth/settings')
    return response.data
  },

  /**
   * Update User Settings
   * PUT /api/v1/auth/settings
   */
  async updateUserSettings(params: UpdateUserSettingsParams): Promise<UserSettings> {
    const response = await apiClient.put<UserSettings>('/api/v1/auth/settings', params)
    return response.data
  },

  /**
   * Приглашение по токену из письма (DEV-1308).
   * GET /api/v1/auth/invitations/token/{token}
   *
   * Метод публичный: экран принятия открывается без сессии. Токен в ответе
   * сервер зануляет, а сам ответ показывает только почту — ролей по их id
   * экран всё равно не назовёт, справочник ролей под `roles:read`.
   */
  async getInvitationByToken(token: string): Promise<InvitationPreview> {
    const { data } = await apiClient.get<{ email?: string; expires_at?: unknown }>(
      `/api/v1/auth/invitations/token/${encodeURIComponent(token)}`,
    )

    return { email: data.email ?? '' }
  },

  /**
   * Принятие приглашения: заводит учётку с ролями из приглашения и сразу
   * отдаёт пару токенов (DEV-1308).
   * POST /api/v1/auth/invitations/accept
   *
   * Имена полей ответа — протобуфные (`access_token`), в отличие от логина, где
   * поля так и названы в proto (`accessToken`). Это не опечатка: gateway отдаёт
   * ровно те имена, что стоят в схеме.
   */
  async acceptInvitation(payload: AcceptInvitationPayload): Promise<AuthTokens> {
    const { data } = await apiClient.post<{ access_token?: string; refresh_token?: string }>(
      '/api/v1/auth/invitations/accept',
      {
        token: payload.token,
        first_name: payload.firstName,
        last_name: payload.lastName,
        password: payload.password,
      },
    )

    return {
      accessToken: data.access_token ?? '',
      refreshToken: data.refresh_token ?? '',
    }
  },
}
